import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';

/**
 * UI Responsiveness System
 * Aligns with "CRITICAL ISSUES REMEDIATION" Item 1.
 * Provides debounced state updates synchronized with requestAnimationFrame.
 */

interface UIResponsivenessContextType {
  scheduleUpdate: (callback: () => void) => void;
  useSmoothState: <T>(initialState: T) => [T, (newState: T) => void];
}

const UIResponsivenessContext = createContext<UIResponsivenessContextType | null>(null);

export const UIResponsivenessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pendingUpdates = useRef<Set<() => void>>(new Set());
  const rafId = useRef<number | null>(null);

  const processUpdates = useCallback(() => {
    pendingUpdates.current.forEach(callback => callback());
    pendingUpdates.current.clear();
    rafId.current = null;
  }, []);

  const scheduleUpdate = useCallback((callback: () => void) => {
    pendingUpdates.current.add(callback);
    
    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(processUpdates);
    }
  }, [processUpdates]);

  // Hook for components to use smooth state updates
  const useSmoothState = <T,>(initialState: T): [T, (newState: T) => void] => {
    const [state, setState] = useState<T>(initialState);
    
    const setSmoothState = useCallback((newState: T) => {
      scheduleUpdate(() => {
        setState(newState);
      });
    }, []);

    return [state, setSmoothState];
  };

  return (
    <UIResponsivenessContext.Provider value={{ scheduleUpdate, useSmoothState }}>
      {children}
    </UIResponsivenessContext.Provider>
  );
};

export const useUIResponsiveness = () => {
  const context = useContext(UIResponsivenessContext);
  if (!context) {
    throw new Error('useUIResponsiveness must be used within a UIResponsivenessProvider');
  }
  return context;
};

// HOC to wrap components with responsiveness checks if needed
export const withResponsiveness = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => (
    <UIResponsivenessProvider>
      <Component {...props} />
    </UIResponsivenessProvider>
  );
};
